import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { journalSequencesApi } from '../services/api/endpoints/journalSequences';
import type {
  JournalSequence,
  UpdateJournalSequenceConfiguration,
} from '../types/journal-sequences';

/**
 * Hook de gestion des séquences de numérotation des pièces (DGI/SYSCOHADA).
 *
 * Charge la liste pour l'entreprise courante (filtrable par exercice) et
 * expose la mise à jour du préfixe / padding. Le compteur courant n'est
 * volontairement pas modifiable côté UI : le backend n'expose pas l'écriture
 * directe pour préserver la chronologie.
 */
export function useJournalSequences(fiscalYearId?: string) {
  const [sequences, setSequences] = useState<JournalSequence[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await journalSequencesApi.list(
        fiscalYearId ? { fiscalYearId } : undefined,
      );
      const payload =
        (response as any)?.data?.data ??
        (response as any)?.data ??
        response;
      const list = Array.isArray(payload) ? payload : payload?.data ?? [];
      setSequences(list);
    } catch (err: any) {
      const message =
        err?.message || err?.error || 'Erreur lors du chargement des séquences';
      setError(message);
      setSequences([]);
    } finally {
      setLoading(false);
    }
  }, [fiscalYearId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updateConfiguration = useCallback(
    async (
      id: string,
      changes: UpdateJournalSequenceConfiguration,
    ): Promise<boolean> => {
      try {
        setSaving(true);
        const response = await journalSequencesApi.update(id, changes);
        const updated =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const payload: JournalSequence | undefined = updated?.data ?? updated;
        if (payload?.id) {
          setSequences((prev) =>
            prev.map((seq) => (seq.id === payload.id ? payload : seq)),
          );
        } else {
          await reload();
        }
        toast.success('Configuration de la séquence mise à jour');
        return true;
      } catch (err: any) {
        const message =
          err?.message ||
          err?.error ||
          'Erreur lors de la mise à jour de la séquence';
        toast.error(message);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [reload],
  );

  return {
    sequences,
    loading,
    saving,
    error,
    reload,
    updateConfiguration,
  };
}
